/**
 * Billing — workspace subscription dashboard.
 *
 * Single-tenant model: shows the current plan, period_end, trial countdown,
 * and a Cancel button (PayPal subscriptions are cancelled directly via our
 * /billing/cancel endpoint — no equivalent of the Stripe Customer Portal).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CreditCard, Sparkles, Copy, ArrowRight, BadgePercent,
  Calendar, AlertTriangle, CheckCircle2, Loader2,
  Download, Archive, Lock, XCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Layout from '../components/Layout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  none: { label: 'Aucun abonnement', tone: 'amber' },
  approval_pending: { label: 'En attente d\'approbation PayPal', tone: 'blue' },
  trialing: { label: 'Essai gratuit en cours', tone: 'blue' },
  active: { label: 'Actif', tone: 'emerald' },
  suspended: { label: 'Suspendu', tone: 'red' },
  cancelled: { label: 'Annulé', tone: 'neutral' },
  expired: { label: 'Expiré', tone: 'neutral' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function daysUntil(iso) {
  if (!iso) return null;
  const d = Math.ceil((new Date(iso) - Date.now()) / 86400000);
  return d;
}

export default function Billing() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [referral, setReferral] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [backups, setBackups] = useState(null);
  const [runningBackup, setRunningBackup] = useState(false);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/billing/status`, { withCredentials: true }),
        axios.get(`${API}/billing/referral/me`, { withCredentials: true }),
      ]);
      setStatus(s.data);
      setReferral(r.data);
      // Backup list — silent failure if user is not admin
      axios.get(`${API}/admin/backup/list`, { withCredentials: true })
        .then(rb => setBackups(rb.data))
        .catch(() => setBackups({ items: [] }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement');
    }
  };

  const downloadGdprExport = async () => {
    setExporting(true);
    try {
      const resp = await axios.post(`${API}/billing/export-data`, {}, {
        withCredentials: true,
        responseType: 'blob',
      });
      // Extract filename from Content-Disposition or fall back
      const cd = resp.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `aegis_export_${Date.now()}.zip`;
      const blob = new Blob([resp.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success('Export téléchargé');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur export');
    } finally {
      setExporting(false);
    }
  };

  const runBackupNow = async () => {
    setRunningBackup(true);
    try {
      const { data } = await axios.post(`${API}/admin/backup/run`, {}, { withCredentials: true });
      toast.success(`Sauvegarde créée : ${data.filename}`);
      const rb = await axios.get(`${API}/admin/backup/list`, { withCredentials: true });
      setBackups(rb.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur sauvegarde');
    } finally {
      setRunningBackup(false);
    }
  };

  const downloadBackup = async (filename) => {
    try {
      const resp = await axios.get(`${API}/admin/backup/download/${encodeURIComponent(filename)}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([resp.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur téléchargement');
    }
  };

  const cancelSubscription = async () => {
    if (!window.confirm("Annuler définitivement l'abonnement ? L'accès cessera immédiatement (PayPal n'a pas d'option « fin de période »).")) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/billing/cancel`, { reason: 'Annulé depuis le portail Aegis' }, { withCredentials: true });
      toast.success("Abonnement annulé");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'annulation");
    } finally {
      setCancelling(false);
    }
  };

  if (!status || !referral) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
        </div>
      </Layout>
    );
  }

  const meta = STATUS_LABELS[status.subscription_status] || STATUS_LABELS.none;
  const isActiveLike = ['active', 'trialing'].includes(status.subscription_status);
  const daysToTrialEnd = daysUntil(status.trial_end);
  const shareUrl = `${window.location.origin}/?ref=${referral.code}`;
  const fmtMoney = (n) => `${(n || 0).toLocaleString('fr-FR')} €`;

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl mx-auto pb-12" data-testid="billing-page">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">Mon abonnement</h1>
          <p className="text-neutral-500 mt-1">Gérez votre plan AEGIS Pro et vos parrainages</p>
        </div>

        {/* Subscription card */}
        <div className="border rounded-xl bg-white p-6 space-y-4" data-testid="subscription-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 text-sm">
                <CreditCard className="w-4 h-4 text-neutral-400" />
                <span className="text-neutral-500">Statut</span>
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium tracking-wide bg-${meta.tone}-100 text-${meta.tone}-700`}>
                  {meta.label}
                </span>
              </div>
              <div className="text-2xl font-semibold mt-1">
                AEGIS Pro {status.cycle === 'year' ? '— Annuel' : status.cycle === 'month' ? '— Mensuel' : ''}
              </div>
              {status.cycle && (
                <div className="text-sm text-neutral-500 mt-1">
                  {status.cycle === 'year' ? `${fmtMoney(status.annual_eur)} HT / an` : `${fmtMoney(status.monthly_eur)} HT / mois`}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {!isActiveLike && status.subscription_status !== 'cancelled' ? (
                <Button onClick={() => navigate('/pricing')} data-testid="goto-pricing-btn">
                  Voir les plans <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              ) : isActiveLike ? (
                <Button onClick={cancelSubscription} disabled={cancelling} variant="outline" className="text-red-600 hover:bg-red-50 border-red-200" data-testid="cancel-subscription-btn">
                  {cancelling && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  <XCircle className="w-4 h-4 mr-1" />
                  Annuler l'abonnement
                </Button>
              ) : null}
            </div>
          </div>

          {/* Trial countdown */}
          {status.subscription_status === 'trialing' && daysToTrialEnd !== null && (
            <div className="border-t pt-4 flex items-center gap-3">
              <Calendar className="w-5 h-5 text-blue-600 shrink-0" />
              <div className="text-sm">
                <div className="font-medium text-neutral-900">Essai gratuit</div>
                <div className="text-neutral-500 text-[12px]">
                  Il vous reste <strong className="text-blue-700">{Math.max(0, daysToTrialEnd)} jour(s)</strong>. Le 1er paiement aura lieu le {fmtDate(status.trial_end)}.
                </div>
              </div>
            </div>
          )}

          {/* Period end */}
          {isActiveLike && status.subscription_status !== 'trialing' && status.current_period_end && (
            <div className="border-t pt-4 flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
              <div className="text-sm text-neutral-600">
                Prochain renouvellement le <strong>{fmtDate(status.current_period_end)}</strong>.
              </div>
            </div>
          )}

          {status.subscription_status === 'suspended' && (
            <div className="border-t pt-4 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
              <div className="text-sm">
                <div className="font-medium text-red-900">Abonnement suspendu</div>
                <div className="text-red-800/80 text-[12px]">Plusieurs paiements ont échoué. Contactez votre banque ou souscrivez à nouveau depuis la page Tarifs.</div>
              </div>
            </div>
          )}

          <p className="border-t pt-4 text-[11px] text-neutral-400 leading-relaxed">
            {status.invoice_footer}
          </p>
        </div>

        {/* Referral card */}
        <div className="border rounded-xl bg-gradient-to-br from-violet-50/50 to-white p-6 space-y-5" data-testid="referral-card">
          <div className="flex items-start gap-3">
            <Sparkles className="w-6 h-6 text-violet-600 shrink-0 mt-0.5" />
            <div>
              <h2 className="font-heading text-xl text-neutral-900">Parrainage</h2>
              <p className="text-sm text-neutral-600 mt-1">
                Partagez votre lien : votre filleul bénéficie de <strong>-50% le 1er mois</strong>, vous recevez <strong>1 mois offert</strong> dès qu'il souscrit.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <KPI label="Code" value={referral.code} mono />
            <KPI label="Parrainages réussis" value={referral.referees_count} />
            <KPI label="Mois offerts gagnés" value={referral.credits_earned} highlight />
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wide text-neutral-500 font-medium mb-1.5">Votre lien à partager</div>
            <div className="flex items-stretch gap-2">
              <input
                value={shareUrl}
                readOnly
                className="flex-1 font-mono text-xs bg-white border rounded px-3 py-2"
                onClick={(e) => e.target.select()}
                data-testid="referral-share-url"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(shareUrl);
                  toast.success('Lien copié — prêt à partager !');
                }}
                data-testid="copy-referral-btn"
              >
                <Copy className="w-3.5 h-3.5 mr-1" /> Copier
              </Button>
            </div>
          </div>

          <div className="text-[12px] text-neutral-500 border-t pt-3 flex items-start gap-2">
            <BadgePercent className="w-4 h-4 text-violet-500 shrink-0 mt-0.5" />
            <span>
              <strong>Pas de plafond</strong> : 12 filleuls = 12 mois offerts = 1 année AEGIS gratuite. Les crédits s'appliquent automatiquement à votre prochaine facture (cycle mensuel) ou en crédit balance (cycle annuel).
            </span>
          </div>
        </div>

        {/* GDPR export — Niveau 2 */}
        <div className="border rounded-xl bg-white p-6 space-y-4" data-testid="gdpr-card">
          <div className="flex items-start gap-3">
            <Download className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <h2 className="font-heading text-xl text-neutral-900">Mes données — Export RGPD</h2>
              <p className="text-sm text-neutral-600 mt-1">
                Téléchargez à tout moment une copie complète de vos données AEGIS (clients, leads, mots-clés, conversions, contacts…) au format JSON. Conforme à l'article 20 du RGPD.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <Button onClick={downloadGdprExport} disabled={exporting} data-testid="export-data-btn">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
              Télécharger toutes mes données
            </Button>
            <span className="text-[11px] text-neutral-500 flex items-center gap-1">
              <Lock className="w-3 h-3" /> Archive {backups?.encrypted_by_default ? 'chiffrée AES-128 (Fernet)' : 'non chiffrée'}, format ZIP + JSON gzippé
            </span>
          </div>
        </div>

        {/* Continuity backup — Niveau 3 (admin) */}
        {backups && (
          <div className="border rounded-xl bg-white p-6 space-y-4" data-testid="backup-card">
            <div className="flex items-start gap-3">
              <Archive className="w-6 h-6 text-blue-700 shrink-0 mt-0.5" />
              <div className="flex-1">
                <h2 className="font-heading text-xl text-neutral-900">Sauvegardes système</h2>
                <p className="text-sm text-neutral-600 mt-1">
                  Sauvegarde automatique chaque <strong>dimanche à 03h00 UTC</strong>. Les {backups.retention_count} dernières archives sont conservées (~3 mois). {backups.encrypted_by_default && <>Toutes les archives sont <strong>chiffrées</strong> avec votre clé Fernet.</>}
                </p>
              </div>
              <Button
                onClick={runBackupNow}
                disabled={runningBackup}
                variant="outline"
                size="sm"
                data-testid="run-backup-btn"
              >
                {runningBackup ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Archive className="w-4 h-4 mr-2" />}
                Lancer maintenant
              </Button>
            </div>

            {backups.items.length === 0 ? (
              <div className="text-sm text-neutral-500 text-center py-6 bg-neutral-50 rounded">
                Aucune sauvegarde encore. Cliquez "Lancer maintenant" pour créer la 1ère.
              </div>
            ) : (
              <div className="border rounded divide-y text-sm">
                {backups.items.map((it) => (
                  <div key={it.filename} className="flex items-center justify-between gap-3 p-2.5 hover:bg-neutral-50">
                    <div className="flex items-center gap-3 min-w-0">
                      <Archive className="w-4 h-4 text-neutral-400 shrink-0" />
                      <div className="min-w-0">
                        <div className="font-mono text-[12px] text-neutral-800 truncate">{it.filename}</div>
                        <div className="text-[11px] text-neutral-500">
                          {new Date(it.modified_at).toLocaleString('fr-FR')} · {it.size_human}
                          {it.encrypted && <span className="ml-2 inline-flex items-center gap-0.5 text-emerald-700"><Lock className="w-3 h-3" /> chiffrée</span>}
                        </div>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => downloadBackup(it.filename)}
                      data-testid={`download-backup-${it.filename}`}
                    >
                      <Download className="w-3.5 h-3.5 mr-1" /> Télécharger
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[11px] text-neutral-500 leading-relaxed border-t pt-3">
              💡 <strong>Bonne pratique</strong> : téléchargez 1 archive par mois sur un disque externe ou un service cloud personnel (Drive, Dropbox). En cas d'incident majeur, vous pourrez restaurer les données. Pour décrypter une archive <code className="font-mono bg-neutral-100 px-1">.zip.enc</code> : utilisez la clé Fernet stockée dans <code className="font-mono bg-neutral-100 px-1">BACKUP_ENCRYPTION_KEY</code> côté serveur.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}

function KPI({ label, value, mono, highlight }) {
  return (
    <div className={`border rounded-lg p-3 bg-white ${highlight ? 'border-violet-200' : 'border-neutral-200'}`}>
      <div className="text-[10px] uppercase tracking-wide text-neutral-500 font-medium">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${mono ? 'font-mono text-base' : 'tabular-nums'} ${highlight ? 'text-violet-700' : 'text-neutral-900'}`}>{value}</div>
    </div>
  );
}
